import React, { useEffect, useRef, useState } from "react";
import {
  FaMicrophone,
  FaStop,
  FaTrashAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";

import "./ServiceOrderAudioRecorder.css";

function pickRecorderMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/aac",
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported(candidates[i])) {
      return candidates[i];
    }
  }
  return "audio/webm";
}

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${mins}:${secs}`;
}

function ServiceOrderAudioRecorder({
  audioBlob,
  onAudioChange,
  t,
  label,
  hint,
  className = "service-order-audio",
}) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");
  const [micReady, setMicReady] = useState(false);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stopStream();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, []);

  useEffect(() => {
    if (!audioBlob) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl("");
      }
      return;
    }
    const url = URL.createObjectURL(audioBlob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [audioBlob]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const handleStart = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error(t("Seu navegador não suporta gravação de áudio."), {
        position: "bottom-right",
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      setMicReady(true);

      const mimeType = pickRecorderMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        onAudioChange(blob);
        stopStream();
        setRecording(false);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((current) => current + 1);
      }, 1000);
    } catch (error) {
      stopStream();
      toast.error(t("Não foi possível acessar o microfone."), {
        position: "bottom-right",
      });
    }
  };

  const handleStop = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
  };

  const handleClear = () => {
    onAudioChange(null);
    setElapsed(0);
  };

  return (
    <div className={`create-schedule-field ${className}`}>
      <label>
        <FaMicrophone style={{ marginRight: 6 }} />
        {label || t("Áudio da ordem")}
        <span className="service-order-location__optional">
          {" "}
          ({t("opcional")})
        </span>
      </label>
      <p className="create-schedule-hint">
        {hint ||
          t(
            "Grave um áudio reforçando a receita. O operador ouve no app junto com a descrição."
          )}
      </p>

      <div className="service-order-audio__controls">
        {!recording ? (
          <button
            type="button"
            className="btn btn-default service-order-audio__record-btn"
            onClick={handleStart}
          >
            <FaMicrophone style={{ marginRight: 6 }} />
            {audioBlob ? t("Gravar novamente") : t("Gravar áudio")}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-danger service-order-audio__record-btn"
            onClick={handleStop}
          >
            <FaStop style={{ marginRight: 6 }} />
            {t("Parar")} ({formatDuration(elapsed)})
          </button>
        )}

        {audioBlob && !recording ? (
          <button
            type="button"
            className="btn btn-link service-order-audio__clear-btn"
            onClick={handleClear}
          >
            <FaTrashAlt style={{ marginRight: 6 }} />
            {t("Remover áudio")}
          </button>
        ) : null}
      </div>

      {previewUrl && !recording ? (
        <audio
          className="service-order-audio__player"
          controls
          preload="metadata"
          src={previewUrl}
        />
      ) : null}

      {micReady && !recording && !audioBlob ? (
        <p className="service-order-audio__status">{t("Microfone pronto")}</p>
      ) : null}
    </div>
  );
}

export default ServiceOrderAudioRecorder;
